import { inlineCommandBarOverflowAriaLabel } from './PeekEventContent.locstring.json';
import { FontWeights } from '@uifabric/styling';
import { observer } from 'mobx-react-lite';
import { CommandBarButton, IButtonProps } from '@fluentui/react/lib/Button';
import { ThemeProvider } from '@fluentui/react-theme-provider';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString, themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import loc from 'owa-localize';
import { getPalette, getPaletteAsRawColors } from 'owa-theme';
import { OwaPalette } from 'owa-theme-shared';
import * as React from 'react';
import FilterableCommandBar, {
    FilterableCommandBarProps,
} from 'owa-filterable-menu/lib/components/FilterableCommandBar';

export interface InlineFormsCommandBarProps extends FilterableCommandBarProps {
    colorScheme: ColorScheme;
}

export interface InlineFormsCommandBarButtonProps extends IButtonProps {
    colorScheme: ColorScheme;
    isOverflowButton: boolean;
}

/**
 * Inline Forms command bar variant used in Time Panel Peek
 */
const InlineFormsCommandBar = observer(function (props: InlineFormsCommandBarProps) {
    const { colorScheme } = props;

    const CustomizedFormsCommandBarButton = (props: IButtonProps) => (
        <InlineFormsCommandBarButton
            colorScheme={colorScheme}
            isOverflowButton={false}
            {...props}
        />
    );

    const CustomizedFormsCommandBarOverflowButton = (props: IButtonProps) => (
        <InlineFormsCommandBarButton colorScheme={colorScheme} isOverflowButton={true} {...props} />
    );

    return (
        // wrapper ensures that the command bar paints/reflows with correct boundaries on first render to handle overflow case correctly
        <div style={{ boxSizing: 'border-box', width: '100%' }}>
            <ThemeProvider theme={colorScheme ? themeFromColorScheme(colorScheme) : null}>
                <FilterableCommandBar
                    buttonAs={CustomizedFormsCommandBarButton}
                    overflowButtonAs={CustomizedFormsCommandBarOverflowButton}
                    elipisisAriaLabel={loc(inlineCommandBarOverflowAriaLabel)}
                    styles={{
                        root: {
                            background: 'transparent',
                            height: 32,
                            margin: '0 0 0 -4px',
                            padding: 0,
                            width: '100%',
                        },
                    }}
                    {...props}
                />
            </ThemeProvider>
        </div>
    );
});

const InlineFormsCommandBarButton = observer(function (props: InlineFormsCommandBarButtonProps) {
    // this is a workaround to handle limitations in FilterableCommandBar
    // Join button is the only item with primary button styling and can be uniquely distinguished by truthy href prep
    const isPrimaryButton = !!props.href;

    const {
        rootColor,
        pressedColor,
        rootBgColor,
        rootHoveredBgColor,
        rootExpandedBgColor,
        rootPressedBgColor,
    } = props.colorScheme
        ? getColorSchemeFromProps(props.colorScheme, isPrimaryButton)
        : getColorSchemeFromTheme(getPalette(), isPrimaryButton);

    const { isOverflowButton } = props;

    return (
        <CommandBarButton
            {...props}
            styles={{
                ...props.styles,
                root: {
                    color: rootColor,
                    backgroundColor: rootBgColor,
                    borderRadius: 2,
                    height: 32,
                    margin: '0 4px',
                },

                rootHovered: {
                    color: pressedColor,
                    backgroundColor: rootHoveredBgColor,
                },

                rootPressed: {
                    color: pressedColor,
                    backgroundColor: rootPressedBgColor,
                },

                rootExpanded: {
                    color: pressedColor,
                    backgroundColor: rootExpandedBgColor,
                },

                rootExpandedHovered: {
                    color: pressedColor,
                    backgroundColor: rootExpandedBgColor,
                },

                flexContainer: {
                    margin: isOverflowButton ? '0 4px' : '0 8px',
                },

                icon: {
                    color: rootColor,
                },

                iconHovered: {
                    color: pressedColor,
                },

                iconPressed: {
                    color: pressedColor,
                },

                iconExpanded: {
                    color: pressedColor,
                },

                iconExpandedHovered: {
                    color: pressedColor,
                },

                label: {
                    fontWeight: FontWeights.semibold,
                },

                menuIcon: {
                    color: rootColor,
                },

                menuIconHovered: {
                    color: pressedColor,
                },

                menuIconPressed: {
                    color: pressedColor,
                },

                menuIconExpanded: {
                    color: pressedColor,
                },

                menuIconExpandedHovered: {
                    color: pressedColor,
                },
            }}
        />
    );
});

// NOTE: inline command bar colors are +1 shade darker than standard command bar
function getColorSchemeFromProps(colorScheme: ColorScheme, isPrimary: boolean) {
    if (isPrimary) {
        return {
            rootColor: getPalette().white,
            pressedColor: getOptimalTextColorString(
                colorScheme.calDarker,
                colorScheme,
                getPaletteAsRawColors().white
            ),
            rootBgColor: colorScheme.accessibleText.msColorWhiteBackground,
            rootHoveredBgColor: colorScheme.calDark,
            rootExpandedBgColor: colorScheme.calDark,
            rootPressedBgColor: colorScheme.calDarker,
        };
    } else {
        return {
            rootColor: getOptimalTextColorString(
                colorScheme.calLighter,
                colorScheme,
                colorScheme.calDark
            ),
            pressedColor: getOptimalTextColorString(
                colorScheme.calTertiary,
                colorScheme,
                colorScheme.calDarker
            ),
            rootBgColor: colorScheme.calLighter,
            rootHoveredBgColor: colorScheme.calLight,
            rootExpandedBgColor: colorScheme.calLight,
            rootPressedBgColor: colorScheme.calTertiary,
        };
    }
}

function getColorSchemeFromTheme(themeSymbols: OwaPalette, isPrimary: boolean) {
    if (isPrimary) {
        return {
            rootColor: themeSymbols.white,
            pressedColor: themeSymbols.white,
            rootBgColor: themeSymbols.themePrimary,
            rootHoveredBgColor: themeSymbols.themeDark,
            rootExpandedBgColor: themeSymbols.themeDark,
            rootPressedBgColor: themeSymbols.themeDarker,
        };
    } else {
        return {
            rootColor: themeSymbols.themeDark,
            pressedColor: themeSymbols.themeDarker,
            rootBgColor: themeSymbols.themeLighterAlt,
            rootHoveredBgColor: themeSymbols.themeLighter,
            rootExpandedBgColor: themeSymbols.themeLighter,
            rootPressedBgColor: themeSymbols.themeLight,
        };
    }
}

export default InlineFormsCommandBar;
